import React from 'react';
import { motion } from 'framer-motion';
import { Plane, Mail, MessageCircle, Calendar, ArrowRight, Phone } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const EnrollmentSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { enrollmentType, studentName, schoolName } = location.state || {};

    const handleBackToHome = () => {
        navigate('/');
    };

    const handleViewSchools = () => {
        navigate('/schools');
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-12">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl mx-auto text-center"
                >
                    {/* Success Icon */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-8"
                    >
                        <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Plane size={48} className="text-blue-600" />
                        </div>
                    </motion.div>

                    {/* Success Message */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mb-8"
                    >
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Thank You for Signing Up!
                        </h1>
                        <p className="text-xl text-gray-600 mb-6">
                            Stay tuned for emails and texts as we get closer to launch.
                        </p>
                    </motion.div>

                    {/* Stay Tuned Message */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        className="bg-white rounded-2xl shadow-lg p-8 mb-8"
                    >
                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Plane size={32} className="text-blue-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                We're Getting Ready for Launch!
                            </h2>
                            <p className="text-gray-600 text-lg mb-6">
                                You'll receive emails and text messages as we get closer to launch with updates about regional availability and early access benefits.
                            </p>
                            <div className="bg-blue-50 rounded-lg p-4">
                                <p className="text-blue-800 font-medium">
                                    🎉 Early sign-ups get a 1,200 CFA discount and priority access when we launch in your region!
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contact Information */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.8 }}
                        className="bg-primary-50 rounded-2xl p-6 mb-8"
                    >
                        <h3 className="text-lg font-semibold text-primary-900 mb-3">
                            Have Questions?
                        </h3>
                        <p className="text-primary-700 mb-4">
                            If you have any questions, feel free to reach out to our team.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="/contact-form"
                                className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <Mail size={16} />
                                <span>Email Us</span>
                            </a>
                            <a
                                href="tel:+2204040966"
                                className="inline-flex items-center space-x-2 px-4 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                            >
                                <Phone size={16} />
                                <span>Call Us</span>
                            </a>
                        </div>
                    </motion.div>

                    {/* Action Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 1.0 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <button
                            onClick={handleBackToHome}
                            className="inline-flex items-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            <span>Back to Home</span>
                        </button>
                        <button
                            onClick={handleViewSchools}
                            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            <span>View Schools</span>
                            <ArrowRight size={16} />
                        </button>
                    </motion.div>

                    {/* Footer Note */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 1.2 }}
                        className="mt-8 text-sm text-gray-500"
                    >
                        <p>
                            Thank you for being part of the AMHAS community. Together, we're building a healthier future for Gambian children.
                        </p>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default EnrollmentSuccess;
